/* ***************************************************************************
 * CON-1694 — the draft attachment shape, and the voice memo marker in it.
 *
 * WHY THIS IS ITS OWN MODULE. The bug was a round trip: what autosave WROTE
 * and what rehydration READ disagreed about whether the attachment was a
 * recording. Both halves lived inside a component, so nothing could test the
 * two against each other, and the disagreement shipped. Here they sit side by
 * side and a test can drive the actual round trip.
 *
 * THE FAILURE THIS CLOSES. isVoiceMemo is React state and dies with the
 * component; the attachment is persisted. Autosave is debounced one second, so
 * a memo left in the compose box for longer went to
 * contactChatPreferences.draftAttachments as filename and container only. On
 * return the draft restored the attachment and never restored the marker, so
 * the send carried isVoiceMemo false. messagesController is strict === true
 * with no server-side inference (deliberately), so it took the Chat Manual
 * branch and never transcoded — and the recipient got a raw webm no handset
 * can play. Not charging for it was the smaller half.
 *
 * WHY NOT INFER FROM THE FILENAME. The recorder names files
 * voice-memo-<epoch>.webm, which is deterministic, and inferring would have
 * needed no schema change at all. It was rejected because createFileName only
 * strips [^a-z0-9.-], so a consultant who uploads "voice-memo-notes.webm"
 * would be charged the automated rate and have their file re-encoded to mono
 * 16 kHz. A naming convention would have become load-bearing for billing.
 *
 * draftAttachments is jsonb and messagesController writes the array through
 * verbatim, so carrying the fact costs no migration and no server change.
 *************************************************************************** */

/* ***************************************************************************
 * buildDraftAttachmentEntry
 *
 * The shape autosave persists. Returns null when there is nothing to save so
 * the caller's "is the draft empty" check stays a length test.
 *************************************************************************** */
export const buildDraftAttachmentEntry = ({ attachment, container, isVoiceMemo } = {}) => {
  if (!attachment) return null;
  return {
    filename: attachment,
    container: container || null,
    isVoiceMemo: !!isVoiceMemo,
  };
};

/* ***************************************************************************
 * readDraftVoiceMemoFlag
 *
 * Reads the marker back. Drafts saved BEFORE this shipped have no isVoiceMemo
 * key: they read false and keep the old behaviour, which is correct rather
 * than merely safe. We cannot know what those attachments were, and guessing
 * would risk charging someone the automated rate for an ordinary upload. The
 * cost is that a recording already sitting in a draft box has to be recorded
 * again — one such draft existed on Production when this shipped.
 *************************************************************************** */
export const readDraftVoiceMemoFlag = (draftAttachment) => !!draftAttachment?.isVoiceMemo;

/* ***************************************************************************
 * normalizeDraftAttachments / draftStateKey
 *
 * The autosave guard compares "what is in the box now" against "what was last
 * saved" and skips the write when they match. Both sides MUST be built the
 * same way or the guard silently stops working.
 *
 * It nearly did. The saved-state key was seeded from the RAW draftAttachments
 * the server returned, while the comparison key was built by
 * buildDraftAttachmentEntry — which always writes isVoiceMemo. A draft saved
 * before that key existed therefore never matched, and switching into that
 * conversation fired an extra database write a second later.
 *
 * There is a second, worse version of the same trap: draftAttachments is
 * jsonb, and Postgres does not preserve key insertion order — it orders by key
 * length, then bytes. Today "filename" (8), "container" (9), "isVoiceMemo"
 * (11) happen to round-trip in the order the builder writes them, so a raw
 * JSON.stringify comparison happens to work. That is luck. Add a key whose
 * length reorders the set and every conversation switch writes to the
 * database, forever, with nothing to show why.
 *
 * Normalising both sides through one function removes both failure modes:
 * key order comes from this code rather than from Postgres, and a missing
 * isVoiceMemo is filled in identically wherever it is read.
 *************************************************************************** */
export const normalizeDraftAttachments = (attachments) => (
  (Array.isArray(attachments) ? attachments : [])
    .map((a) => buildDraftAttachmentEntry({
      attachment: a?.filename,
      container: a?.container,
      isVoiceMemo: a?.isVoiceMemo,
    }))
    .filter(Boolean)
);

export const draftStateKey = (message, attachments) => JSON.stringify({
  message: message || '',
  attachments: normalizeDraftAttachments(attachments),
});

/* ***************************************************************************
 * isRecordedAttachment — the voice memo marker, DERIVED rather than stored.
 *
 * WHY IT IS DERIVED. isVoiceMemo used to be a boolean held beside the
 * attachment, and every writer of the attachment had to remember to keep the
 * two in step. Three times one did not: the marker was lost on a draft round
 * trip (CON-1694), tangled with the discard sentinel, and on 2026-10-01 it was
 * STUCK after a successful send — resetForm() emptied the attachment but not
 * the marker, which lives outside Formik, so the next attachment, a photo,
 * went out as a memo and failed in ffmpeg. Nine Production failures.
 *
 * Now nothing stores "this is a memo". The compose box remembers only WHICH
 * file the recorder produced, and the marker is true if and only if the
 * current attachment is that file. A reset, a photo, a paste, a template or
 * the x all change the attachment, so the marker turns false by itself and no
 * writer has to remember anything. The CP Chat app works the same way, which
 * is why it never had this bug.
 *
 * THIS IS NOT THE FILENAME INFERENCE CON-1694 REJECTED. That would have asked
 * "does this name LOOK like a recording" — a pattern an ordinary upload named
 * voice-memo-notes.webm could match, charging someone for a file they
 * attached from disk. This asks "is this the EXACT file the recorder just
 * uploaded" — an identity check against a unique, timestamped blob name the
 * recorder itself created. A look-alike never matches. If you are here because
 * a filename comparison looks like the thing we said not to do: it is not.
 *************************************************************************** */
export const isRecordedAttachment = (attachment, recordedAttachment) => (
  !!attachment && !!recordedAttachment && attachment === recordedAttachment
);

/* What the recorders produce: webm / ogg (Chrome, Edge, Firefox), mp4 (Safari),
 * m4a (CP Chat iOS). Used ONLY to drop a marker on restore, never to add one —
 * see recordedAttachmentFromDraft. */
const RECORDING_EXTENSIONS = ['webm', 'weba', 'ogg', 'oga', 'opus', 'mp4', 'm4a', 'aac', 'wav'];

const extensionOf = (fileName) => (
  (String(fileName || '').toLowerCase().match(/\.([a-z0-9]+)$/) || [])[1] || ''
);

/* ***************************************************************************
 * recordedAttachmentFromDraft
 *
 * On draft restore, which file (if any) the recorder produced. Returns the
 * filename when the draft entry carries the marker, else null.
 *
 * WHY THE EXTENSION CHECK. Before this shipped, autosave persisted whatever
 * the marker said — including a STUCK one — so a draft could hold a photo
 * flagged as a memo. sp 661 hit exactly that: the same JPEG failed on 09-27
 * and again two days later on 09-29, restored from the draft. A marker on a
 * file that is not audio is therefore dropped here, so such a draft heals the
 * moment it is opened rather than failing again.
 *
 * The check can only DROP a marker, never ADD one: a recording-shaped file
 * without the marker is returned as null, an ordinary attachment. So it can
 * never charge for an upload, which is the line CON-1694 drew.
 *************************************************************************** */
export const recordedAttachmentFromDraft = (draftAttachment) => {
  const filename = draftAttachment?.filename;
  if (!filename || !readDraftVoiceMemoFlag(draftAttachment)) return null;
  return RECORDING_EXTENSIONS.includes(extensionOf(filename)) ? filename : null;
};
